import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import ReactSelect, { createFilter, SingleValue } from "react-select";
import { useLocation } from "react-router-dom";

import { CREATE_URL_REDIRECT } from "../../graphql/customMutations";
import { LIST_VERIFIED_RESTAURANTS } from "../../graphql/customQueries";
import { useAuth } from "../../context/auth-context";
import { PageWrapper } from "../../tabin/components/pageWrapper";
import { Input } from "../../tabin/components/input";
import { Button } from "../../tabin/components/button";
import { toast } from "../../tabin/components/toast";

import "./configureQRRedirect.scss";

export const ConfigureQRRedirect = () => {
    const [originalUrl, setOriginalUrl] = useState("");
    const [tableNumber, setTableNumber] = useState("");
    const [restaurantId, setRestaurantId] = useState("");
    const [restaurants, setRestaurants] = useState<{ id: string; name: string }[]>([]);
    const [restaurantsLoading, setRestaurantsLoading] = useState(false);
    const [restaurantsError, setRestaurantsError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { isSales, isSupport } = useAuth();

    const [createUrlRedirect, { loading }] = useMutation(CREATE_URL_REDIRECT);
    const location = useLocation();
    const {
        data: listRestaurantsData,
        loading: listRestaurantsLoading,
        error: listRestaurantsError,
        fetchMore: fetchMoreRestaurants,
    } = useQuery(LIST_VERIFIED_RESTAURANTS, {
        variables: {
            filter: { verified: { eq: true } },
            limit: 200,
            nextToken: null,
        },
        fetchPolicy: "network-only",
        skip: !(isSales || isSupport),
    });

    const restaurantOptions = useMemo(() => restaurants.map((restaurant) => ({ value: restaurant.id, label: restaurant.name })), [restaurants]);
    const selectedRestaurant = restaurantOptions.find((option) => option.value === restaurantId) ?? null;
    const isLoadingRestaurants = restaurantsLoading || listRestaurantsLoading;

    const buildRedirectUrl = (restaurant: string, table: string) => {
        const trimmedTable = table.trim();
        return trimmedTable ? `https://order.tabin.co.nz/${restaurant}?table=${encodeURIComponent(trimmedTable)}` : `https://order.tabin.co.nz/${restaurant}`;
    };

    const redirectUrl = useMemo(() => (restaurantId ? buildRedirectUrl(restaurantId, tableNumber) : ""), [restaurantId, tableNumber]);

    useEffect(() => {
        let cancelled = false;

        const hydrateRestaurants = async () => {
            if (!listRestaurantsData) return;

            setRestaurantsLoading(true);
            setRestaurantsError(null);

            try {
                let items: { id: string; name: string; verified: boolean }[] = listRestaurantsData.listRestaurants?.items ?? [];
                let nextToken = listRestaurantsData.listRestaurants?.nextToken;

                while (nextToken) {
                    const more = await fetchMoreRestaurants({
                        variables: {
                            filter: { verified: { eq: true } },
                            limit: 200,
                            nextToken,
                        },
                    });

                    if (cancelled) return;

                    const moreItems = more?.data?.listRestaurants?.items ?? [];
                    items = items.concat(moreItems);
                    nextToken = more?.data?.listRestaurants?.nextToken;
                }

                const verified = items.filter((r) => r && r.verified).map((r) => ({ id: r.id, name: r.name }));

                const sorted = verified.sort((a, b) => a.name.localeCompare(b.name));
                setRestaurants(sorted);

                if (!restaurantId && sorted.length) {
                    setRestaurantId(sorted[0].id);
                }
            } catch (err: any) {
                if (!cancelled) setRestaurantsError(err?.message || "Failed to load restaurants.");
            } finally {
                if (!cancelled) setRestaurantsLoading(false);
            }
        };

        hydrateRestaurants();

        return () => {
            cancelled = true;
        };
    }, [fetchMoreRestaurants, listRestaurantsData, restaurantId]);

    useEffect(() => {
        if (listRestaurantsError) {
            setRestaurantsError(listRestaurantsError.message);
        }
    }, [listRestaurantsError]);

    useEffect(() => {
        if (originalUrl) return;

        const state = location.state as { from?: string } | undefined;
        const fromState = state?.from;
        const fromReferrer = document.referrer || "";

        if (fromState) {
            setOriginalUrl(fromState);
        } else if (fromReferrer) {
            setOriginalUrl(fromReferrer);
        }
    }, [location.state, originalUrl]);

    const onChangeRestaurant = (option: SingleValue<{ value: string; label: string }>) => {
        setRestaurantId(option?.value || "");
    };

    const validateUrl = (url: string) => {
        try {
            const parsedUrl = new URL(url);
            return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
        } catch {
            return false;
        }
    };

    const onSave = async () => {
        setError(null);

        if (!originalUrl || !redirectUrl) {
            setError("Both the original and redirect URLs are required.");
            return;
        }

        if (!restaurantId) {
            setError("Select a restaurant to associate this redirect.");
            return;
        }

        if (!tableNumber.trim()) {
            setError("Table number is required.");
            return;
        }

        if (!validateUrl(originalUrl) || !validateUrl(redirectUrl)) {
            setError("Please provide valid URLs that include http:// or https://.");
            return;
        }

        try {
            await createUrlRedirect({
                variables: {
                    originalUrl,
                    redirectUrl,
                    restaurantId,
                },
            });

            toast.success("QR redirect saved.");
            setTableNumber("");
        } catch (err: any) {
            setError(err?.message || "Failed to save redirect. Please try again.");
        }
    };

    return (
        <PageWrapper>
            <div className="configure-qr-redirect">
                <div className="h2 mb-2">Configure QR Redirect</div>

                <div className="mb-3">
                    <div className="text-bold mb-2">Restaurant</div>
                    <ReactSelect
                        className="basic-select"
                        classNamePrefix="select"
                        isClearable
                        isLoading={restaurantsLoading || listRestaurantsLoading}
                        options={restaurantOptions}
                        value={selectedRestaurant}
                        onChange={onChangeRestaurant}
                        placeholder={isLoadingRestaurants ? "Loading restaurants..." : "Select a restaurant"}
                        filterOption={createFilter({ ignoreAccents: false })}
                        isDisabled={isLoadingRestaurants || loading}
                        name="restaurant"
                    />
                    {restaurantsError && <div className="text-error mt-2">{restaurantsError}</div>}
                </div>

                <div className="mb-3">
                    <Input
                        label="Table Number"
                        placeholder="Table number"
                        value={tableNumber}
                        onChange={(event) => setTableNumber(event.target.value)}
                        disabled={isLoadingRestaurants || loading}
                    />
                </div>

                <div className="mb-3">
                    <div className="text-bold mb-2">Original URL</div>
                    <div className="text-grey">{originalUrl || "No URL detected"}</div>
                </div>

                <div className="mb-3">
                    <div className="text-bold mb-2">Redirect URL</div>
                    <div className="text-grey">{redirectUrl || "No redirect URL generated"}</div>
                </div>

                {error && <div className="text-error mb-3">{error}</div>}

                <Button onClick={onSave} loading={loading} disabled={loading || isLoadingRestaurants || !restaurants.length}>
                    Save Redirect
                </Button>
            </div>
        </PageWrapper>
    );
};
